import dynamicAPI from './dynamicAPI';
import { useMutation, useQueryClient } from 'react-query';
import useAuth from '../hooks/useAuth';
import { useMessage } from '../context/AdminMessageContext';

const url = '/api/auth';

// Opaque "remember this device" token for 2FA. Persisted across logout so a
// trusted device skips the code for ~3 months (the server enforces expiry).
const DEVICE_TOKEN_KEY = 'deviceToken';
const getDeviceToken = () => {
    try { return localStorage.getItem(DEVICE_TOKEN_KEY) || undefined; } catch { return undefined; }
};
const saveDeviceToken = (t) => {
    try { if (t) { localStorage.setItem(DEVICE_TOKEN_KEY, t); } } catch { /* storage blocked */ }
};

const useAuthHook = () => {
    const { setAuth } = useAuth();
    const queryClient = useQueryClient();
    const { setSuccessMessage, setErrorMessage } = useMessage();


    const postLogin = useMutation(
        async (obj) => await dynamicAPI('post', `${url}/login`, { ...obj, deviceToken: getDeviceToken() }),
        {
            retry: 0,
            onSuccess: (data) => {
                // When 2FA is required the login isn't complete yet — the
                // second step (code entry) is handled in the Login screen.
                if (data?.twoFactorRequired) { return; }
                queryClient.invalidateQueries(['login'])
                setSuccessMessage('Login successful.')
            },
            onError: (error) => {
                const errorMessage = error?.error || 'An error occurred during login.'
                setErrorMessage(errorMessage)
            },
        }
    )

    // Second login step: exchange the challenge token + authenticator code
    // (or a backup code) for the real access token.
    const postLoginVerify = useMutation(
        async (obj) => await dynamicAPI('post', `${url}/login/2fa`, obj),
        {
            retry: 0,
            onSuccess: (data) => {
                // Remember this device so future logins skip the code.
                if (data?.deviceToken) { saveDeviceToken(data.deviceToken); }
                queryClient.invalidateQueries(['login'])
                setSuccessMessage('Login successful.')
            },
            onError: (error) => {
                const errorMessage = error?.error || 'Invalid code. Please try again.'
                setErrorMessage(errorMessage)
            },
        }
    )

    const postLogout = () => {
        localStorage.removeItem('userRole')
        localStorage.removeItem('userInfo')
        // Keep object shape stable
        setAuth({ userInfo: null, userRole: null });

        // Clear react-query cache so user-dependent queries stop
        queryClient.clear();

        setSuccessMessage?.('Logged out successfully.');
        return true
    }

    const postForgotPassword = useMutation(
        async (obj) => await dynamicAPI('post', `${url}/forgot-password`, obj),
        {
            retry: 0,
            onSuccess: () => {
                queryClient.invalidateQueries(['forgot password']);
                setSuccessMessage('Password reset has been sent.');
            },
            onError: () => {
                // Keep the message generic regardless of the server reason
                // (no linked Telegram, delivery failure, etc.).
                setErrorMessage('Please contact the administrator.');
            },
        }
    )

    const postResetPassword = useMutation(
        async (obj) => await dynamicAPI('post', `${url}/reset-password`, obj),
        {
            retry: 0,
            onSuccess: () => {
                queryClient.invalidateQueries(['reset password']);
                setSuccessMessage('Password reset successfully.');
            },
            onError: (error) => {
                const errorMessage = error?.error || 'An error occurred while resetting the password.';
                setErrorMessage(errorMessage);
            },
        }
    )

    const postGoogleLogin = useMutation(
        async ({ credential, invite }) =>
            await dynamicAPI('post', `${url}/google`, { credential, invite, deviceToken: getDeviceToken() }),
        {
            retry: 0,
            onSuccess: (data) => {
                // 2FA pending → login isn't complete; the code step follows.
                if (data?.twoFactorRequired) { return; }
                queryClient.invalidateQueries(['login']);
                setSuccessMessage('Login successful.');
            },
            onError: (error) => {
                const errorMessage = error?.error || 'Google login failed.';
                setErrorMessage(errorMessage);
            },
        }
    );

  return { postLogin, postLoginVerify, postLogout, postForgotPassword, postResetPassword, postGoogleLogin }
}

export default useAuthHook
